import { IProduct } from 'models';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const FREQUENT_PRODUCTS_URL = `${API_BASE_URL}/api/frequent-products/`;
const FAVOURITE_PRODUCTS_URL = `${API_BASE_URL}/api/favourite-products/`;
const productFavouriteUrl = (productId: number) =>
  `${API_BASE_URL}/api/products/${productId}/favourite/`;

interface IApiProduct {
  id: number;
  sku: string | null;
  name_raw: string;
  name_zh: string;
  name_en: string;
  package_spec: string;
  unit: string;
  price: string | number | null;
  image?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  is_favourite?: boolean;
}

interface IApiCategory {
  id: number | string;
  name_zh: string;
  name_en: string;
  products: IApiProduct[];
}

interface IFrequentProductsResponse {
  categories: IApiCategory[];
}

const getCategoryName = (category: IApiCategory) =>
  category.name_zh || category.name_en || 'Frequent products';

const getProductTitle = (product: IApiProduct) =>
  product.name_zh ||
  product.name_en ||
  product.name_raw ||
  `Product ${product.id}`;

const getProductImageUrl = (product: IApiProduct) => {
  const imageUrl = product.image_url || product.imageUrl || product.image || '';

  return imageUrl.trim() || undefined;
};

const mapProduct = (product: IApiProduct, category: IApiCategory): IProduct => {
  const categoryNameZh = category.name_zh || category.name_en || 'Frequent products';
  const categoryNameEn = category.name_en || category.name_zh || 'Frequent products';
  const categoryName = getCategoryName(category);

  return {
    id: product.id,
    sku: product.sku,
    title: getProductTitle(product),
    description: categoryName,
    availableSizes: [],
    style: categoryName,
    price: product.price === null ? null : Number(product.price),
    installments: 0,
    currencyId: 'EUR',
    currencyFormat: '€',
    isFreeShipping: false,
    isFavourite: Boolean(product.is_favourite),
    categoryId: category.id,
    categoryName,
    categoryNameZh,
    categoryNameEn,
    imageUrl: getProductImageUrl(product),
    packageSpec: product.package_spec,
    unit: product.unit,
  };
};

export const getProducts = async () => {
  const response = await fetch(FREQUENT_PRODUCTS_URL);

  if (!response.ok) {
    throw new Error('Could not load frequent products.');
  }

  const data: IFrequentProductsResponse = await response.json();

  return data.categories.flatMap((category) =>
    category.products.map((product) => mapProduct(product, category))
  );
};

export const getFavouriteProducts = async (accessToken: string) => {
  const response = await fetch(FAVOURITE_PRODUCTS_URL, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Could not load favourite products.');
  }

  const data: IFrequentProductsResponse = await response.json();

  return data.categories.flatMap((category) =>
    category.products.map((product) => mapProduct(product, category))
  );
};

export const setProductFavourite = async (
  productId: number,
  isFavourite: boolean,
  accessToken: string
) => {
  const response = await fetch(productFavouriteUrl(productId), {
    method: isFavourite ? 'POST' : 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Could not update favourite product.');
  }
};
